import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Mail, MapPin, Award } from 'lucide-react';
import { useLang } from '../context/LangContext';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { GlassCard } from '../components/shared/GlassCard';
import { GhostButton } from '../components/shared/GhostButton';
import { fadeInUp, SPRING } from '../animations/variants';

export default function ContactPage() {
  const { t } = useLang();

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-8 bg-[#0A0A0A] dot-grid overflow-hidden">
          <motion.div className="absolute rounded-full blur-[100px] opacity-20 bg-brand-green w-80 h-80 top-1/4 right-1/3"
            animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />

          <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full border border-brand-green/40 bg-brand-green/10 text-brand-green text-sm font-medium mb-8"
              data-testid="contact-badge">
              {t("contact.hero.badge")}
            </motion.div>

            <motion.h1 className="font-extrabold tracking-[-0.03em] leading-[1.05] text-white mb-6"
              style={{ fontSize: 'clamp(42px, 7vw, 72px)' }}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ ...SPRING, delay: 0.1 }}>
              {t("contact.hero.title")}
            </motion.h1>

            <motion.p className="text-neutral-400 text-lg max-w-xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ ...SPRING, delay: 0.2 }}>
              {t("contact.hero.subtitle")}
            </motion.p>
          </div>
        </section>

        {/* Split: Form + Info */}
        <section data-testid="contact-split" className="py-16 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Form iframe - 3 cols */}
            <motion.div className="lg:col-span-3" {...fadeInUp}>
              <GlassCard className="p-2 overflow-hidden" hover={false}>
                <iframe
                  src="https://link.crococlick.com/widget/form/jQR0BjsdQ7MMKXyxnt2D"
                  style={{ width: '100%', height: '754px', border: 'none', borderRadius: '22px' }}
                  id="inline-jQR0BjsdQ7MMKXyxnt2D"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="SaaS Registration"
                  data-height="754"
                  data-layout-iframe-id="inline-jQR0BjsdQ7MMKXyxnt2D"
                  data-form-id="jQR0BjsdQ7MMKXyxnt2D"
                  title="SaaS Registration"
                  data-testid="contact-form-iframe"
                />
              </GlassCard>
            </motion.div>

            {/* Info - 2 cols */}
            <motion.div className="lg:col-span-2 space-y-6" {...fadeInUp}>
              {/* Calendly */}
              <GlassCard className="p-6" hover={true}>
                <h3 className="text-lg font-semibold text-white mb-3">{t("contact.calendly.title")}</h3>
                <GhostButton href="https://calendly.com/elite-help" external={true} data-testid="contact-calendly-btn" className="w-full justify-center">
                  <Calendar size={18} />{t("contact.calendly.cta")}
                </GhostButton>
              </GlassCard>

              {/* Info */}
              <GlassCard className="p-6" hover={false}>
                <h3 className="text-lg font-semibold text-white mb-4">{t("contact.info.title")}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-sm text-neutral-400">
                    <Mail size={16} className="text-brand-green flex-shrink-0" />
                    <span>{t("contact.info.email")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-neutral-400">
                    <MapPin size={16} className="text-brand-green flex-shrink-0" />
                    <span>{t("contact.info.location")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-neutral-400">
                    <Award size={16} className="text-brand-green flex-shrink-0" />
                    <span>{t("contact.info.certif")}</span>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
